import React from "react";
import { useNavigate } from "react-router-dom";
import { Icon } from "@/components/Icon";
import { useAuth } from "@/context/AuthContext";
import { useMobilePrefs } from "./MobilePrefs";
import { fmtCompact } from "./fmtCompact";
import pnrIcon from "@/assets/pnrbet-icon.png";

/* MobileTopBar — port of the design's PMTop component (source:
   .design-handoff/source/raw/PMTop.dc.html). Two real variants:
     • app  — P mark · (centered) currency pill with fused + cashier · avatar
     • back — back chevron · title · compact active-balance pill
   Per Ev: NO header search affordance — search already lives on the Casino
   screen (its own search+filter row), so a second global one is redundant. The
   user avatar (right) stays — it opens the account menu (same initials rule as
   the desktop Shell: username|email → first 2 chars, uppercased).
   The design's simulated iOS status bar (9:41 + signal/wifi/battery) is DEVICE
   chrome drawn by the OS, not app UI — we replace it with real safe-area padding.
   Balances are the REAL user wallet; streamer mode masks them. */

export default function MobileTopBar({ mode = "app", title, onOpenCashier, onOpenCurrency, onOpenMenu }) {
  const { user, streamerMode } = useAuth();
  const { isCash } = useMobilePrefs();
  const navigate = useNavigate();

  const mask = !!streamerMode;
  const initials = (user?.username || user?.email || "PN").slice(0, 2).toUpperCase();
  // Compact so a huge balance abbreviates (1.23M / 1.23B) instead of clipping the pill.
  const bal = isCash
    ? fmtCompact(user?.cash ?? 0, "cash", { mask })
    : fmtCompact(user?.coins ?? 0, "coins", { mask });
  const curIcon = isCash ? "pnrcash" : "pnrcoin";
  const curColor = isCash ? "#E4E4E7" : "#00D632";

  const barWrap = {
    position: "sticky", top: 0, zIndex: 20,
    paddingTop: "var(--m-safe-top)",
    background: "rgba(10,10,10,.82)",
    backdropFilter: "blur(24px)", WebkitBackdropFilter: "blur(24px)",
    boxShadow: "0 1px 0 rgba(255,255,255,.05)",
  };

  if (mode === "back") {
    return (
      <div style={barWrap}>
        <div style={{ height: 53, display: "flex", alignItems: "center", gap: 4, padding: "0 12px 0 4px" }}>
          <button
            type="button" aria-label="Back" onClick={() => navigate(-1)}
            style={{ width: 44, height: 44, flex: "none", display: "flex", alignItems: "center", justifyContent: "center", background: "none", border: 0, cursor: "pointer" }}
          >
            <Icon name="chevron-left" size={24} className="text-white" />
          </button>
          <span style={{ flex: 1, minWidth: 0, font: "800 18px 'Manrope'", letterSpacing: "-.02em", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
            {title}
          </span>
          <button
            type="button" onClick={onOpenCurrency} aria-label="Switch play currency"
            style={{ height: 36, maxWidth: 170, boxSizing: "border-box", display: "flex", alignItems: "center", gap: 6, padding: "0 12px 0 9px", borderRadius: 12, background: "#141417", boxShadow: "inset 0 0 0 1px rgba(255,255,255,.07)", border: 0, cursor: "pointer" }}
          >
            <Icon name={curIcon} size={18} style={{ color: curColor, flex: "none" }} />
            <span className="m-tnum" style={{ font: "800 14px 'Manrope'", color: "#fff", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", minWidth: 0 }}>{bal}</span>
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={barWrap}>
      <div style={{ height: 53, display: "flex", alignItems: "center", gap: 6, padding: "0 8px 0 12px" }}>
        <button
          type="button" aria-label="PRYMEBET home" onClick={() => navigate("/")}
          style={{ width: 44, height: 44, flex: "none", display: "flex", alignItems: "center", background: "none", border: 0, cursor: "pointer" }}
        >
          <img src={pnrIcon} alt="PRYMEBET" style={{ width: 34, height: 34, display: "block" }} />
        </button>
        <div style={{ flex: 1 }} />
        <div
          style={{ height: 42, maxWidth: 230, minWidth: 0, boxSizing: "border-box", display: "flex", alignItems: "center", gap: 8, padding: "0 4px 0 10px", borderRadius: 14, background: "linear-gradient(180deg,#1A1A1E 0%,#121215 100%)", boxShadow: "inset 0 0 0 1px rgba(255,255,255,.08),inset 0 1px 0 rgba(255,255,255,.06)" }}
        >
          <button
            type="button" onClick={onOpenCurrency} aria-label="Switch play currency"
            style={{ alignSelf: "stretch", minWidth: 0, display: "flex", alignItems: "center", gap: 8, background: "none", border: 0, cursor: "pointer", padding: "0 2px 0 0" }}
          >
            <Icon name={curIcon} size={22} style={{ color: curColor, flex: "none" }} />
            <span className="m-tnum" style={{ font: "800 15px 'Manrope'", letterSpacing: "-.01em", color: "#fff", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", minWidth: 0 }}>{bal}</span>
            <Icon name="chevron-down" size={12} style={{ color: "#71717a", flex: "none" }} />
          </button>
          <button
            type="button" onClick={onOpenCashier} aria-label="Cashier — get coins or redeem"
            style={{ width: 34, height: 34, borderRadius: 10, background: "#00D632", boxShadow: "inset 0 1px 0 rgba(255,255,255,.32)", display: "flex", alignItems: "center", justifyContent: "center", border: 0, cursor: "pointer" }}
          >
            <Icon name="plus" size={16} style={{ color: "#0A0A0A" }} />
          </button>
        </div>
        <div style={{ flex: 1 }} />
        <button
          type="button" onClick={onOpenMenu} aria-label="Account menu"
          style={{ width: 44, height: 44, flex: "none", display: "flex", alignItems: "center", justifyContent: "center", background: "none", border: 0, cursor: "pointer" }}
        >
          <span
            aria-hidden="true"
            style={{ width: 36, height: 36, borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", background: "#26262B", boxShadow: "inset 0 0 0 2px #0A0A0A, 0 0 0 1.5px rgba(255,255,255,.08)", font: "800 13px 'Manrope'", color: "#E4E4E7", letterSpacing: ".01em" }}
          >
            {initials}
          </span>
        </button>
      </div>
    </div>
  );
}
